import type { ReactNode } from 'react'
import { useCallback, useState } from 'react'
import { Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { AddProductComplianceEvidenceDialog } from '@/v2/components/product-compliance-briefing/AddProductComplianceEvidenceDialog'
import { ProductComplianceEvidenceListItem } from '@/v2/components/product-compliance-briefing/ProductComplianceEvidenceListItem'

export type ProductComplianceFileAttachment = {
  id: string
  fileName: string
  contentType: string | null
  fileSizeKb: number | null
  uploadedAt: string
  uploadedByUserId: string | null
}

type ProductComplianceFileSlotStepProps = {
  canWrite: boolean
  isLoading: boolean
  files: ProductComplianceFileAttachment[]
  isDeletePending: boolean
  evidenceSlotTitle: string
  onUploadFile: (file: File) => Promise<unknown>
  onDeleteFile: (attachmentId: string) => Promise<unknown>
  stepShell: (props: {
    actions?: ReactNode
    children?: ReactNode
  }) => ReactNode
}

export function ProductComplianceFileSlotStep({
  canWrite,
  isLoading,
  files,
  isDeletePending,
  evidenceSlotTitle,
  onUploadFile,
  onDeleteFile,
  stepShell,
}: ProductComplianceFileSlotStepProps) {
  const { t } = useTranslation('common')
  const [dialogOpen, setDialogOpen] = useState(false)
  const [uploadingFileNames, setUploadingFileNames] = useState<Set<string>>(
    () => new Set()
  )

  const handleUploadFiles = useCallback(
    async (uploadFiles: File[]) => {
      for (const file of uploadFiles) {
        setUploadingFileNames((current) => new Set(current).add(file.name))
        try {
          await onUploadFile(file)
        } catch {
          toast.error(
            t('v2.pages.products.detail.sharedEvidence.actions.uploadFailed')
          )
        } finally {
          setUploadingFileNames((current) => {
            const next = new Set(current)
            next.delete(file.name)
            return next
          })
        }
      }
    },
    [onUploadFile, t]
  )

  const handleDelete = useCallback(
    async (attachmentId: string) => {
      try {
        await onDeleteFile(attachmentId)
      } catch {
        toast.error(t('v2.pages.products.detail.drawings.actions.deleteFailed'))
      }
    },
    [onDeleteFile, t]
  )

  const hasFiles = files.length > 0

  const actionLabel = t('v2.pages.products.detail.sharedEvidence.actions.upload')

  return (
    <>
      {stepShell({
        actions: canWrite ? (
          <Button
            type='button'
            variant='outline'
            size='sm'
            className='gap-1'
            onClick={() => {
              setDialogOpen(true)
            }}
          >
            {hasFiles ? <Plus className='size-4' /> : null}
            {actionLabel}
          </Button>
        ) : undefined,
        children: (
          <>
            {isLoading ? <Skeleton className='h-10 w-full' /> : undefined}
            {hasFiles ? (
              <ul className='space-y-2'>
                {files.map((file) => (
                  <li key={file.id}>
                    <ProductComplianceEvidenceListItem
                      file={file}
                      isUploading={uploadingFileNames.has(file.fileName)}
                      deleteDisabled={!canWrite || isDeletePending}
                      onDelete={() => handleDelete(file.id)}
                    />
                  </li>
                ))}
              </ul>
            ) : undefined}
          </>
        ),
      })}
      {canWrite ? (
        <AddProductComplianceEvidenceDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          slotTitle={evidenceSlotTitle}
          onUploadFiles={handleUploadFiles}
        />
      ) : null}
    </>
  )
}
